/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-empty-function */
import { PrinterOutlined, UploadOutlined } from "@ant-design/icons";
import { BaseKey, GetOneResponse, useNotification } from "@refinedev/core";
import { QueryObserverResult } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Col,
  Form,
  FormProps,
  Input,
  Modal,
  ModalProps,
  Row,
  Typography,
  Upload,
} from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useChangeStatusPaymentRequest } from "../../api/sendmoney";
import { ROOT_URL } from "../../App";
import {
  getPurposeOfRemittanceOptionLabel,
  purposeOfRemittanceItems,
  purposeOfRemittancePlaceholder,
} from "../../components/inputs/select/purposeOfRemittance.constants";
import { SelectField } from "../../components/inputs/select/select";
import {
  getStatusOptionLabel,
  paymentRequestPossibleMoves,
} from "../../components/inputs/select/statusSelect.constants";
import TextContent from "../../components/modal/admin/text-content";
import { TOKEN_KEY } from "../../components/providers/authProvider";
import PaymentStatus from "../../types/paymentStatus.type";
import { acceptUploadFileTypes } from "../../utils/acceptUploadFileTypes";
import { beforeUpload } from "../../utils/beforeUpload";
import {
  clearFieldErrorMessageOnChange,
  getFileListFromEvent,
} from "../../utils/form.constants";
import { useIsAdminRole } from "../../utils/hooks/useIdentityAdmin.hook";
import { useNotificationOkCancel } from "../../utils/hooks/useNotificationOkCancel.hook";
import {
  isEditable,
  paymentStatusColors,
  paymentStatusNames,
} from "../../utils/paymentStatus.utils";
import { confirmModalUnsavedChangesProps } from "../components/constants";
import {
  IDocumentsChanges,
  PaymentRequestsEditForm,
} from "../components/interface";

interface IProps {
  formProps: FormProps;
  modalProps: ModalProps;
  queryResult?: QueryObserverResult<GetOneResponse<PaymentRequestsEditForm>>;
  id?: BaseKey;
  resetEditingId: () => void;
  formIsLoading: boolean;
  close: () => void;
  refetchListSendMoney: any;
}

export const Fiat = [
  'USD',
  'EUR',
  'HKD',
  'AUD',
  'JPY',
  'CHF',
  'CNY',
  'AED',
  'SGD',
  'GBP',
];
export const Crypto = ["USDT", "DAI", "ETH"];

export const PaymentRequestEdit = ({
  modalProps,
  formProps,
  queryResult,
  id,
  resetEditingId,
  formIsLoading,
  close,
  refetchListSendMoney,
}: IProps) => {
  const { open: openNotification } = useNotification();
  const isAdminRole = useIsAdminRole();
  const [prevPaymentRequest, setPrevPaymentRequest] = useState<any>();
  const [formChanged, setFormChanged] = useState<boolean>(false);
  const status = Form.useWatch("status", { form: formProps.form });
  const [initialDocuments, setInitialDocuments] = useState<string[]>([]);

  const { mutate, isLoading } = useChangeStatusPaymentRequest({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification({
          key: "success_transaction_confirm",
          message: response?.data?.message || "Change status successfully!",
          type: "success",
        });
      }
      refetchListSendMoney();
      queryResult?.refetch();
    },
    onError: (error: { message: string; reason_code: string }) => {
      if (openNotification) {
        openNotification({
          key: "error",
          message: error?.message || "Change status failed!",
          type: "error",
        });
      }
    },
  });

  const onClose = useCallback(() => {
    close();
  }, [close]);

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onClose,
  });

  const statusSelectItems = useMemo(
    () =>
      paymentRequestPossibleMoves.get(
        prevPaymentRequest?.status?.current || PaymentStatus.DRAFT
      ) || [PaymentStatus.DRAFT],
    [prevPaymentRequest?.status?.current]
  );

  const afterClose = useCallback(() => {
    setFormChanged(false);
    resetEditingId();
    setPrevPaymentRequest(undefined);
    queryResult?.remove();
  }, [queryResult, resetEditingId]);

  const onSubmit = (values: any) => {
    const { documents } = values;
    const documentChanges: IDocumentsChanges = {
      new: documents
        ?.filter((doc: { response?: any }) => doc.response)
        .map((doc: { response?: any }) => doc.response ?? ""),
      deleted: initialDocuments?.filter(
        (initialFileName: string) =>
          !documents?.find(({ name }: any) => {
            return name === initialFileName;
          })
      ),
    };

    const documentChanged = !!(
      (documentChanges.new && documentChanges.new.length) ||
      documentChanges?.deleted?.length
    );

    const body = {
      id: id || 0,
      status: values.status,
      purposeOfRemittance: values.purposeOfRemittance,
      reason:
        values.status === PaymentStatus.APPROVED ||
          values.status === PaymentStatus.PENDING
          ? null
          : values.reason,
      documentChanges: documentChanged ? documentChanges : undefined,
    };
    mutate(body);
  };

  const onModalCancel = useCallback(() => {
    if (formIsLoading || isLoading) return;
    onClose();
  }, [onClose, formIsLoading, isLoading]);

  const isDisabledForm = !isEditable(
    prevPaymentRequest?.status?.current ?? PaymentStatus.DRAFT
  );

  useEffect(() => {
    if (
      queryResult?.status === "success" &&
      modalProps.open &&
      queryResult?.data?.data
    ) {
      setPrevPaymentRequest(queryResult.data.data);
    }
  }, [queryResult, modalProps.open]);

  useEffect(() => {
    if (prevPaymentRequest) {
      formProps.form?.setFieldsValue({
        totalToPay: prevPaymentRequest?.amounts?.totalSendAmount,
        payCurrency: prevPaymentRequest?.paymentCurrency,
        beneficiaryWillReceive: prevPaymentRequest?.amounts?.totalReceiveAmount,
        receiveCurrency: prevPaymentRequest?.receiveCurrency,
        paymentMethod:
          prevPaymentRequest?.remiter?.type === "INTERNAL"
            ? "Portfolio"
            : "Provider",
        provider: prevPaymentRequest?.remiter?.provider,
        providerBankName: prevPaymentRequest?.remiter?.providerBankName,
        providerBankSwift: prevPaymentRequest?.remiter?.providerBankSwift,
        providerBankAddress: prevPaymentRequest?.remiter?.providerBankAddress,
        providerAccountName: prevPaymentRequest?.remiter?.providerAccountName,
        providerBankAccount: prevPaymentRequest?.remiter?.providerBankAccount,
        providerWalletAddress: prevPaymentRequest?.remiter?.walletAddress,
        providerWalletNetwork: prevPaymentRequest?.remiter?.network,
        referenceNumber: prevPaymentRequest?.referenceNumber,
        beneficiary: prevPaymentRequest?.beneficiary?.companyName,
        bankName: prevPaymentRequest?.beneficiary?.companyName,
        bankSwift: prevPaymentRequest?.beneficiary?.bankSwift,
        bankAddress: prevPaymentRequest?.beneficiary?.bankAddress,
        accountName: prevPaymentRequest?.beneficiary?.companyName,
        bankAccount: prevPaymentRequest?.beneficiary?.bankAccount,
        network: prevPaymentRequest?.beneficiary?.network,
        walletAddress: prevPaymentRequest?.beneficiary?.walletAddress,
        sendDate: prevPaymentRequest?.timestamps?.dateOfTransaction,
        orderTime: prevPaymentRequest?.timestamps?.orderTime,
        status: prevPaymentRequest?.status?.current,
        reason: prevPaymentRequest?.reason,
        purposeOfRemittance: prevPaymentRequest?.purposeOfRemittance,
        smartUID: prevPaymentRequest?.createdByUser?.smartUID,
        documents: prevPaymentRequest?.documentNames?.map(
          (fileName: string) => ({
            name: fileName,
          })
        ),
      });
      setInitialDocuments(prevPaymentRequest?.documentNames ?? []);
    }
  }, [formProps.form, prevPaymentRequest]);

  return (
    <>
      <Modal
        {...modalProps}
        className="smartModalTitle"
        title={`Send money #${id ?? prevPaymentRequest?.id}`}
        destroyOnClose
        maskClosable={false}
        footer={
          <Col>
            <Row
              style={{
                justifyContent: "space-around",
                justifyItems: "center",
                width: "400px",
              }}
            >
              <Button
                key="save"
                type={isAdminRole ? "primary" : undefined}
                onClick={() => formProps?.form?.submit()}
                loading={isLoading || formIsLoading}
                disabled={
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.REJECTED ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.COMPLETED ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.APPROVED ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.PROCESSING ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.FAILED ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.CANCELLED ||
                  statusSelectItems.length <= 1 ||
                  formIsLoading ||
                  isLoading
                }
              >
                Save
              </Button>
              <Button
                key="save"
                onClick={onModalCancel}
                disabled={isLoading || formIsLoading}
                loading={isLoading || formIsLoading}
              >
                Cancel
              </Button>
            </Row>
            {prevPaymentRequest?.printableFormUrl && (
              <Button
                icon={<PrinterOutlined />}
                type={"link"}
                href={prevPaymentRequest?.printableFormUrl}
                target={"_blank"}
              >
                &nbsp;Print
              </Button>
            )}
          </Col>
        }
        onCancel={onModalCancel}
        afterClose={afterClose}
      >
        {unsavedChangesContextHolder}
        <Form
          {...formProps}
          onFinish={onSubmit}
          validateTrigger={false}
          onValuesChange={(e) => {
            setFormChanged(true);
            clearFieldErrorMessageOnChange(formProps?.form)(e);
          }}
          className="detail-withdraw"
        >
          <div
            style={{
              display: "flex",
              width: "100%",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "end",
            }}
          >
            <Badge
              color={
                paymentStatusColors[
                prevPaymentRequest?.status?.current || "PENDING"
                ]
              }
              text={
                paymentStatusNames[
                prevPaymentRequest?.status?.current || "PENDING"
                ]
              }
            />
          </div>
          <hr />
          <TextContent
            label="Total to pay"
            value={`${prevPaymentRequest?.amounts?.totalSendAmount} ${prevPaymentRequest?.paymentCurrency}`}
            boldValue={true}
          />
          <div
            style={{
              display: "flex",
              width: "100%",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "end",
            }}
          >
            <Typography
              className="text-label"
              style={{
                textAlign: "start",
                fontSize: "12px",
                color:
                  prevPaymentRequest?.isConfirmed ||
                    prevPaymentRequest?.status?.current ===
                    PaymentStatus.COMPLETED
                    ? "#18B853"
                    : "rgba(242, 138, 40, 1)",
              }}
            >
              {prevPaymentRequest?.isConfirmed ||
                prevPaymentRequest?.status?.current === PaymentStatus.COMPLETED
                ? "*User has paid"
                : "*User has not paid"}
            </Typography>
          </div>

          <TextContent
            label="Beneficiary Will Receive"
            value={`${prevPaymentRequest?.amounts?.totalReceiveAmount} ${prevPaymentRequest?.receiveCurrency}`}
            boldValue={true}
          />
          <hr />
          <>
            <TextContent label="Payment Method" value="Portfolio" />
            <TextContent
              label="Currency"
              value={prevPaymentRequest?.paymentCurrency || "N/A"}
            />
          </>

          <TextContent
            label="Reference Number"
            value={prevPaymentRequest?.referenceNumber}
          />
          <hr />
          {prevPaymentRequest?.receiveUser ? (
            <>
              <TextContent label="Transfer method" value="SMART UID" />
              <TextContent
                label="Beneficiary"
                value={prevPaymentRequest?.receiveUser?.smartUID}
                boldValue={true}
                boldLabel={true}
              />
            </>
          ) : (
            <>
              <TextContent
                label="Beneficiary"
                value={prevPaymentRequest?.beneficiary?.companyName}
                boldValue={true}
                boldLabel={true}
              />
              {Fiat.includes(prevPaymentRequest?.receiveCurrency) ? (
                <>
                  <TextContent
                    label="Bank Name"
                    value={prevPaymentRequest?.beneficiary?.bankName}
                  />
                  <TextContent
                    label="Bank SWIFT"
                    value={prevPaymentRequest?.beneficiary?.bankSwift}
                  />
                  <TextContent
                    label="Bank Address"
                    value={prevPaymentRequest?.beneficiary?.bankAddress}
                  />
                  <TextContent
                    label="Bank Account"
                    value={prevPaymentRequest?.beneficiary?.bankAccountNumber}
                  />
                </>
              ) : (
                <>
                  <TextContent
                    label="Network"
                    value={prevPaymentRequest?.beneficiary?.network ?? prevPaymentRequest?.beneficiary?.networkName}
                  />
                  <TextContent
                    label="Beneficiary Wallet Address"
                    value={prevPaymentRequest?.beneficiary?.walletAddress}
                  />
                </>
              )}
            </>
          )}
          <TextContent
            label="Send Date"
            value={moment(
              prevPaymentRequest?.timestamps?.dateOfTransaction
            ).format("DD/MM/YYYY")}
          />
          {prevPaymentRequest?.futurePaymentDate && (
            <>
              <TextContent
                label="Future date"
                value={
                  prevPaymentRequest?.futurePaymentDate
                    ? moment(
                      prevPaymentRequest?.futurePaymentDate
                    ).format("DD/MM/YYYY")
                    : ""
                }
              />
            </>
          )}
          <hr />
          <>
            <TextContent
              label="Provider"
              value={prevPaymentRequest?.provider?.provider}
            />
            {Crypto.includes(prevPaymentRequest?.paymentCurrency) ? (
              <>
                <TextContent
                  label="Provider Wallet Network"
                  value={prevPaymentRequest?.provider?.networkName}
                />
                <TextContent
                  label="Provider Wallet Address"
                  value={prevPaymentRequest?.provider?.walletAddress}
                />
              </>
            ) : (
              <>
                <TextContent
                  label="Provider Bank Name"
                  value={prevPaymentRequest?.provider?.bankName}
                />
                <TextContent
                  label="Provider Bank SWIFT"
                  value={prevPaymentRequest?.provider?.bankSwift}
                />
                <TextContent
                  label="Provider Bank Address"
                  value={prevPaymentRequest?.provider?.bankAddress}
                />
                <TextContent
                  label="Provider Bank Account"
                  value={prevPaymentRequest?.provider?.bankAccountNumber}
                />
              </>
            )}
          </>

          <hr />
          <TextContent
            label="Order Time"
            value={moment(prevPaymentRequest?.timestamps?.orderTime).format(
              "HH:mm:ss DD/MM/YYYY"
            )}
          />
          <TextContent
            label="Special instructions"
            value={prevPaymentRequest?.specialInstructions || "N/A"}
          />
          {isAdminRole && (
            <SelectField
              label="Purpose Of Remittance"
              name="purposeOfRemittance"
              getOptionLabel={getPurposeOfRemittanceOptionLabel}
              items={purposeOfRemittanceItems}
              placeholder={purposeOfRemittancePlaceholder}
              allowClear
              rules={[{ required: true, message: "This field is required" }]}
              disabled={
                prevPaymentRequest?.status?.current ===
                PaymentStatus.REJECTED ||
                prevPaymentRequest?.status?.current ===
                PaymentStatus.POSTPONED ||
                statusSelectItems.length <= 1 ||
                isLoading ||
                formIsLoading
              }
            />
          )}
          {isAdminRole && (
            <SelectField
              rules={[{ required: true, message: "This field is required" }]}
              label="Status"
              name="status"
              getOptionLabel={getStatusOptionLabel}
              items={statusSelectItems}
              disabled={
                (prevPaymentRequest?.status?.current !==
                  PaymentStatus.PENDING &&
                  prevPaymentRequest?.status?.current !==
                  PaymentStatus.POSTPONED) ||
                isLoading ||
                formIsLoading
              }
            />
          )}
          {status === PaymentStatus?.REJECTED ||
            status === PaymentStatus.POSTPONED ? (
            <Form.Item
              label="Reason"
              name="reason"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input.TextArea
                rows={2}
                placeholder="Reason"
                disabled={
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.REJECTED ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.POSTPONED ||
                  isLoading ||
                  formIsLoading
                }
              />
            </Form.Item>
          ) : null}
          <Col>
            <Form.Item
              name="documents"
              valuePropName="fileList"
              getValueFromEvent={getFileListFromEvent}
            >
              <Upload
                accept={acceptUploadFileTypes}
                action={`${ROOT_URL}/api/v1/storage/upload`}
                listType="text"
                headers={{
                  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
                }}
                multiple={true}
                beforeUpload={beforeUpload}
                showUploadList={{
                  showRemoveIcon:
                    prevPaymentRequest?.status?.current !==
                      PaymentStatus.DRAFT &&
                      prevPaymentRequest?.status?.current !==
                      PaymentStatus.PENDING
                      ? false
                      : true,
                }}
              >
                <Button
                  icon={<UploadOutlined />}
                  disabled={
                    (prevPaymentRequest?.status?.current !==
                      PaymentStatus.DRAFT &&
                      prevPaymentRequest?.status?.current !==
                      PaymentStatus.PENDING) ||
                    isLoading ||
                    formIsLoading
                  }
                >
                  &nbsp;Click to upload supporting documents
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Form>
      </Modal>
    </>
  );
};
